import { useAuth } from "@/contexts/AuthContext";
import { useChapters, useUpdateChapter } from "@/api/chapters";
import { useState, useMemo, useEffect } from "react";
import {
  LogOut,
  ChevronRight,
  Landmark,
  Mail,
  KeyRound,
  Info,
  Plus,
  Loader2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/* ── Settings Row ── */
function SettingsRow({ icon: Icon, label, value, destructive, onClick }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
        onClick && "hover:bg-muted/50 active:bg-muted",
      )}
    >
      {Icon && (
        <div
          className={cn(
            "h-8 w-8 rounded-md flex items-center justify-center shrink-0 text-sm",
            destructive
              ? "bg-destructive/10 text-destructive"
              : "bg-primary/10 text-primary",
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
      )}
      <span
        className={cn(
          "flex-1 text-sm font-medium",
          destructive ? "text-destructive" : "text-foreground",
        )}
      >
        {label}
      </span>
      {value && (
        <span className="text-xs text-muted-foreground mr-1 shrink-0 max-w-[40%] truncate">
          {value}
        </span>
      )}
      {onClick && !destructive && (
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      )}
    </Comp>
  );
}

/* ── Mahallu List Editor dialog ── */
function MahalluEditor({ open, onClose, list, chapterId, onSave }) {
  const [items, setItems] = useState(list || []);
  const [newItem, setNewItem] = useState("");
  const updateChapter = useUpdateChapter();
  const isSaving = updateChapter.isPending;

  useEffect(() => {
    if (open && list?.length > 0) {
      setItems(list);
    }
  }, [open, list]);

  const add = () => {
    const val = newItem.trim();
    if (val && !items.includes(val)) {
      setItems([...items, val]);
      setNewItem("");
    }
  };

  const handleSave = async () => {
    await updateChapter.mutateAsync(
      {
        id: chapterId,
        mahalluList: items,
      },
      {
        onSuccess: () => {
          onSave?.(items);
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Mahallu List</DialogTitle>
          <DialogDescription>
            Add or manage mahallu names for your chapter
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="flex gap-2">
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Add mahallu name..."
              disabled={isSaving}
            />
            <Button
              size="sm"
              onClick={add}
              disabled={!newItem.trim() || isSaving}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="max-h-60 overflow-y-auto space-y-1">
            {items.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No mahallu names added
              </p>
            )}
            {items.map((item, i) => (
              <div
                key={i}
                className="flex items-center rounded-lg border px-3 py-2 bg-muted/30"
              >
                <span className="flex-1 text-sm">{item}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 gap-2"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const { data: chapters } = useChapters();
  const [showMahallu, setShowMahallu] = useState(false);
  const [mahalluList, setMahalluList] = useState([]);

  const myChapter = useMemo(
    () => (chapters || []).find((ch) => ch._id === user?.chapter),
    [chapters, user],
  );

  console.log("My Chapter:", myChapter);

  useEffect(() => {
    if (myChapter?.mahalluList) {
      setMahalluList(myChapter.mahalluList);
    }
  }, [myChapter]);

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Header ── */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Profile Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your account and chapter information
        </p>
      </div>

      {/* ── Profile card ── */}
      <div className="space-y-5">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-16 w-16 rounded-lg bg-primary/15 text-primary flex items-center justify-center text-lg font-bold shrink-0">
              {(user?.chapterName || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base font-semibold truncate">
                {user?.chapterName || "Chapter Head"}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {user?.email}
              </p>
              <Badge variant="secondary" className="mt-2 text-xs">
                Chapter Administrator
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Chapter Section */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">Chapter</h2>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <SettingsRow
                icon={Landmark}
                label="Manage Mahallu List"
                value={`${mahalluList.length} items`}
                onClick={() => setShowMahallu(true)}
              />
            </CardContent>
          </Card>
        </div>

        {/* Account Section */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">Account</h2>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <SettingsRow icon={Mail} label="Email" value={user?.email} />
              <Separator />
              <SettingsRow
                icon={KeyRound}
                label="Change Password"
                onClick={() => {}}
              />
            </CardContent>
          </Card>
        </div>

        {/* About Section */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">More</h2>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <SettingsRow icon={Info} label="Version" value="1.0.0" />
              <Separator />
              <SettingsRow
                icon={LogOut}
                label="Sign Out"
                destructive
                onClick={logout}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      <MahalluEditor
        open={showMahallu}
        onClose={() => setShowMahallu(false)}
        list={mahalluList}
        chapterId={user?.chapter}
        onSave={setMahalluList}
      />
    </div>
  );
}
